import { readOnly, alias } from '@ember/object/computed';
import { computed } from '@ember/object';
import BillingRecord, { BILLABLE_RECORD } from "./billable-record";
import { attr, hasMany, belongsTo } from '@ember-data/model';
import { SH_BILLABLE_LINE_ITEM } from "./sh-billable-line-item";

const FACTOID_TYPE = 'invoice';

const SIMPLE_STATUS = {
  TO_SEND: 'to_send',
  REVISED: 'revised',
  SENT: 'sent',
  PARTIALLY_PAID: 'partially_paid',
  PAID: 'paid',
  PAID_EXACTLY: 'paid_exactly',
  OVERPAID: 'overpaid',
  UNPAID: 'unpaid',
};

const PAID_STATUS_TEXT = {
  PARTIALLY_PAID: 'Partially Paid',
  PAID_EXACTLY: 'Paid Exactly',
  OVERPAID: 'Overpaid',
  UNPAID: 'Unpaid',
  UNKNOWN: 'Unknown',
};

const PROCESSORS = {
  CREDIT: 'credit',
  INVOICE: 'invoice',
  STRIPE: 'stripe',
};

export const SH_INVOICE = {
  SIMPLE_STATUS,
  PAID_STATUS_TEXT,
  PROCESSORS,
  FACTOID_TYPE,
};

const ShInvoice = BillingRecord.extend({
  idWithRevision: attr('string'),
  isPaidByCc: attr('boolean'),
  lastSentAt: attr('date'),
  memo: attr('string'),
  revisedAt: attr('date'),
  revision: attr('number'),
  sentAt: attr('date'),
  costCodes: attr('object-to-array'),
  storyCodes: attr('object-to-array'),
  overdueAt: attr('date'),

  //relationships
  actions: hasMany('action', {
    async: false,
    inverse: 'actionable',
  }),
  application: belongsTo('application', { async: false }),
  childShInvoices: hasMany('shInvoice', {
    async: false,
    inverse: 'parentShInvoice',
  }),
  company: belongsTo('company', { async: false }),
  expediteRequestor: belongsTo('user', { async: false }),
  feeStructure: belongsTo('feeStructure', { async: false }),
  parentShInvoice: belongsTo('shInvoice', {
    async: false,
    inverse: 'childShInvoices',
  }),
  shInvoiceLineItems: hasMany('shInvoiceLineItem', { async: false }),
  shInvoiceTransactions: hasMany('shInvoiceTransaction', { async: false }),
  shPayment: belongsTo('shPayment', { async: false }),

  /**
   * Convenience method to return our related billable line items.  Named the same so that we can call this method
   * on either ShPayment or ShInvoices if we don't care which type is returned.
   * @return {Ember.A<ShBillableLineItem>}
   */
  shBillableLineItems: readOnly('shInvoiceLineItems'),

  /**
   * This is necessary for cases where we need to compute a property based on the application ids of a collection
   * of ShInvoices.  Ember does not support nested properties after @each.
   */
  applicationId: alias('application.id'),

  /**
   * Calculate the fee for a buyer to expedite the payment related to this invoice
   */
  buyerExpediteFee: computed(
    'shInvoiceLineItems.@each.{amount,type}',
    function () {
      const total = this.get('shInvoiceLineItems').reduce((carry, shInvoiceLineItem) => {
        if (!SH_BILLABLE_LINE_ITEM.EXPEDITE_FEE_TYPES.includes(shInvoiceLineItem.get('type'))) {
          return carry;
        }
        return carry + shInvoiceLineItem.get('amount');
      }, 0);

      return (total * this.get('feeStructure.buyerAdvanceFee')).toFixed(2);
    }
  ),

  /**
   * Returns a path to download a pdf of this billable
   * @type {String}
   */
  downloadPath: computed(
    'id',
    function () {
      return `/sh-invoices/${this.get('id')}/pdf`;
    }
  ),

  /**
   * @type {Boolean}    This IS a batched invoice.
   */
  isBatchedInvoice: computed('type', function() {
    return this.get('type') === BILLABLE_RECORD.TYPES.BATCH;
  }),

  /**
   * @type {Boolean}      True if this invoice is part of a batched invoice.
   */
  isInBatchedInvoice: computed('parentShInvoice.id', function() {
    return !!this.get('parentShInvoice.id');
  }),

  /** @type {Boolean} */
  isPaid: computed('paidAt', function() {
    return !!this.get('paidAt');
  }),

  /** @type {Boolean} */
  isUnpaid: computed('isPaid', function() {
    return !this.isPaid;
  }),

  /** @type {Boolean} */
  isSent: computed('lastSentAt', function () {
    return !!this.get('lastSentAt');
  }),

  /**
   * Get a 'paid' pseudo status class of the ShInvoice
   *
   * @type {String}     of type SH_INVOICE.PAID_STATUS_TEXT.*
   */
  statusPaidText: computed(
  'amount',
  'paidAmount',
  function() {
    let amount = this.get('amount'),
      paidAmount = this.get('paidAmount');

    if (paidAmount > 0 && amount > paidAmount) {
      return SH_INVOICE.PAID_STATUS_TEXT.PARTIALLY_PAID;
    }

    if (amount === paidAmount) {
      return SH_INVOICE.PAID_STATUS_TEXT.PAID_EXACTLY;
    }

    if (amount < paidAmount) {
      return SH_INVOICE.PAID_STATUS_TEXT.OVERPAID;
    }

    if (amount > 0 && amount > paidAmount) {
      return SH_INVOICE.PAID_STATUS_TEXT.UNPAID;
    }

    return SH_INVOICE.PAID_STATUS_TEXT.UNKNOWN;
  }),
});

export default ShInvoice;
